import React, { createContext, useContext, useRef } from 'react'

import { identity } from 'es-toolkit'
import { useStore } from 'zustand'
import { useShallow } from 'zustand/react/shallow'

import type {
  CompaniesStore,
  CompaniesStoreState,
} from '@/components/library-v2/companies/Table/store'
import { createCompaniesStore } from '@/components/library-v2/companies/Table/store'
import { getActiveConfirmationStatusFilters } from '@/components/library-v2/lib'

import type { RelationshipStatus } from '@/types/library/Entity'

interface CompaniesContextProviderProps {
  children: React.ReactNode
}

export const CompaniesContext = createContext<CompaniesStore | undefined>(
  undefined,
)

export const CompaniesContextProvider: React.FC<
  CompaniesContextProviderProps
> = ({ children }) => {
  const storeRef = useRef<CompaniesStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createCompaniesStore()
  }

  return (
    <CompaniesContext.Provider value={storeRef.current}>
      {children}
    </CompaniesContext.Provider>
  )
}

export const useCompaniesContext = () => {
  const context = useContext(CompaniesContext)
  if (!context) {
    throw new Error(
      'useCompaniesContext must be used within a CompaniesContextProvider',
    )
  }
  return context
}

export const useCompaniesStore = <T,>(
  selector: (state: CompaniesStoreState) => T = identity as (
    state: CompaniesStoreState,
  ) => T,
) => {
  const context = useCompaniesContext()
  if (!context) {
    throw new Error(
      'useCompaniesStore must be used within a CompaniesContextProvider',
    )
  }
  return useStore(context, selector)
}

export const useCompaniesFilters = () => {
  return useCompaniesStore(useShallow((state) => state.filters))
}

export const useCompaniesSetFilters = () => {
  return useCompaniesStore((state) => state.setFilters)
}

export const useCompaniesAddRelationshipStatusFilter = () => {
  return useCompaniesStore((state) => state.addRelationshipStatusFilter)
}

export const useCompaniesResetFilters = () => {
  return useCompaniesStore((state) => state.resetFilters)
}

export const useCompaniesIndustryFilters = () => {
  return useCompaniesStore(useShallow((state) => state.filters.industries))
}

export const useCompaniesConfirmationStatusFilters = () => {
  return useCompaniesStore(
    useShallow((state) => state.filters.confirmationStatuses),
  )
}

export const useCompaniesActiveConfirmationStatusFilters = () => {
  return useCompaniesStore(
    useShallow((state) =>
      getActiveConfirmationStatusFilters(state.filters.confirmationStatuses),
    ),
  )
}

export const useCompaniesRelationshipFilters = () => {
  return useCompaniesStore(useShallow((state) => state.filters.relationships))
}

export const useCompaniesRelationshipStatusFilters = () => {
  return useCompaniesStore(
    useShallow((state) => state.filters.relationshipStatuses),
  )
}

export const useCompaniesRelationshipStatusFilterIsActive = (
  status: RelationshipStatus | null,
) => {
  return useCompaniesStore(
    (state) =>
      status !== null && state.filters.relationshipStatuses.includes(status),
  )
}

export const useCompaniesSearchQuery = () => {
  return useCompaniesStore(useShallow((state) => state.searchQuery))
}

export const useCompaniesSetSearchQuery = () => {
  return useCompaniesStore((state) => state.setSearchQuery)
}

export const useCompaniesSelectedCompanyIds = () => {
  return useCompaniesStore(useShallow((state) => state.selectedCompanyIds))
}

export const useCompaniesSetSelectedCompanyIds = () => {
  return useCompaniesStore((state) => state.setSelectedCompanyIds)
}

export const useCompaniesLastSelectedRowId = () => {
  return useCompaniesStore((state) => state.lastSelectedRowId)
}

export const useCompaniesSetLastSelectedRowId = () => {
  return useCompaniesStore((state) => state.setLastSelectedRowId)
}

export const useCompaniesPagination = () => {
  return useCompaniesStore(useShallow((state) => state.pagination))
}

export const useCompaniesSetPagination = () => {
  return useCompaniesStore((state) => state.setPagination)
}

export const useCompaniesDeleteDialogOpen = () => {
  return useCompaniesStore((state) => state.deleteDialogOpen)
}

export const useCompaniesSetDeleteDialogOpen = () => {
  return useCompaniesStore((state) => state.setDeleteDialogOpen)
}

export const useCompaniesMergeDialogOpen = () => {
  return useCompaniesStore((state) => state.mergeDialogOpen)
}

export const useCompaniesSetMergeDialogOpen = () => {
  return useCompaniesStore((state) => state.setMergeDialogOpen)
}

export const useCompaniesConfirmDialogOpen = () => {
  return useCompaniesStore((state) => state.confirmDialogOpen)
}

export const useCompaniesSetConfirmDialogOpen = () => {
  return useCompaniesStore((state) => state.setConfirmDialogOpen)
}

export const useCompaniesCreateDialogOpen = () => {
  return useCompaniesStore((state) => state.createDialogOpen)
}

export const useCompaniesSetCreateDialogOpen = () => {
  return useCompaniesStore((state) => state.setCreateDialogOpen)
}

export const useCompaniesImportDialogOpen = () => {
  return useCompaniesStore((state) => state.importDialogOpen)
}

export const useCompaniesSetImportDialogOpen = () => {
  return useCompaniesStore((state) => state.setImportDialogOpen)
}
